import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth, useTheme, useLanguage } from '../../context/AppContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { toast } from 'sonner';
import SenArtisteLogo from '../../components/SenArtisteLogo';
import LanguageSelector from '../../components/LanguageSelector';
import { 
  Sun, Moon, Users, DollarSign, Ticket, LogOut, 
  Music, TrendingUp, Calendar, ExternalLink, QrCode, Copy, CreditCard, Loader2, AlertCircle, Check
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ApporteurDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, logout, token, loading: authLoading } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t, language } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [stripeStatus, setStripeStatus] = useState(null);

  useEffect(() => {
    // Attendre que l'authentification soit chargée
    if (authLoading) return;
    
    if (!user || user.role !== 'APPORTEUR') {
      navigate('/login');
      return;
    }
    fetchDashboard();
    
    // Vérifier si on revient de Stripe
    const stripeParam = searchParams.get('stripe');
    if (stripeParam === 'success') {
      toast.success('Configuration Stripe réussie !');
    } else if (stripeParam === 'refresh') {
      toast.info('Veuillez compléter votre configuration Stripe');
    }
  }, [user, authLoading, navigate, searchParams]);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API_URL}/apporteur/me/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(response.data);
      
      // Si l'apporteur a Stripe activé, récupérer le statut
      if (response.data.apporteur?.stripe_connect_enabled) {
        fetchStripeStatus(response.data.apporteur.id);
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Erreur lors du chargement du dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchStripeStatus = async (apporteurId) => {
    try {
      const response = await axios.get(`${API_URL}/apporteur/${apporteurId}/stripe/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStripeStatus(response.data);
    } catch (error) {
      console.error('Error fetching Stripe status:', error);
    }
  };

  const handleStripeOnboarding = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/apporteur/${dashboardData.apporteur.id}/stripe/onboarding`,
        { origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.onboarding_url) {
        window.location.href = response.data.onboarding_url;
      } else if (response.data.status === 'active') {
        toast.success('Compte Stripe déjà actif !');
        fetchDashboard();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur Stripe');
    }
  };

  const formatPrice = (price, currency = 'XOF') => {
    if (price === 0 || price === undefined) return currency === 'EUR' ? '0,00 €' : '0 FCFA';
    const currencyCode = currency === 'FCFA' ? 'XOF' : currency;
    return new Intl.NumberFormat('fr-FR', { 
      style: 'currency', 
      currency: currencyCode, 
      minimumFractionDigits: currency === 'EUR' ? 2 : 0 
    }).format(price);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Code copié !');
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center hero-gradient">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-lg">Chargement du tableau de bord...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-card border-b border-border">
        <div className="max-w-7xl mx-auto px-2 sm:px-3 py-1 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <SenArtisteLogo className="w-12 h-12" />
            <span className="font-syne font-bold text-xl">Sen Artiste</span>
            <span className="px-2 py-0.5 text-xs rounded-full bg-primary/20 text-primary">
              {language === 'fr' ? 'Apporteur' : 'Referrer'}
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            <LanguageSelector />
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="theme-toggle"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium">{user?.firstName} {user?.lastName}</p>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
            </div>
            
            <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 md:p-6">
        {/* Titre et code */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="font-syne text-3xl font-bold" data-testid="apporteur-dashboard-title">
            Tableau de bord Apporteur
          </h1>
          <p className="text-muted-foreground mt-2">
            Bienvenue, {dashboardData?.apporteur?.firstName} {dashboardData?.apporteur?.lastName}
          </p>
          
          {/* Code de parrainage */}
          <div className="mt-4 p-4 bg-primary/10 rounded-lg inline-flex items-center gap-4">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Votre code parrainage :</p>
              <p className="font-mono text-2xl font-bold text-primary" data-testid="apporteur-code">
                {dashboardData?.apporteur?.code}
              </p>
            </div>
            <Button 
              variant="outline" 
              size="icon" 
              onClick={() => copyToClipboard(dashboardData?.apporteur?.code)}
              data-testid="copy-code-btn"
            >
              <Copy className="w-4 h-4" />
            </Button>
          </div>
        </motion.div>

        {/* Stats Cards - Revenus EUR et FCFA */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className="glass-card border-l-4 border-l-primary">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Artistes parrainés</p>
                    <p className="text-3xl font-bold" data-testid="artists-count">
                      {dashboardData?.artistsCount || 0}
                    </p>
                  </div>
                  <Users className="w-10 h-10 text-primary opacity-50" />
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
            <Card className="glass-card border-l-4 border-l-blue-500">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Commission EUR</p>
                    <p className="text-2xl font-bold text-blue-500" data-testid="commission-eur">
                      {formatPrice(dashboardData?.totalCommissionEUR || 0, 'EUR')}
                    </p>
                    <p className="text-xs text-muted-foreground">Carte bancaire</p>
                  </div>
                  <DollarSign className="w-10 h-10 text-blue-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card className="glass-card border-l-4 border-l-green-500">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Commission FCFA</p>
                    <p className="text-2xl font-bold text-green-500" data-testid="commission-fcfa">
                      {formatPrice(dashboardData?.totalCommissionFCFA || 0, 'FCFA')}
                    </p>
                    <p className="text-xs text-muted-foreground">Mobile Money (simulé)</p>
                  </div>
                  <DollarSign className="w-10 h-10 text-green-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
            <Card className="glass-card border-l-4 border-l-purple-500">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Billets vendus</p>
                    <p className="text-3xl font-bold text-purple-500" data-testid="tickets-sold">
                      {dashboardData?.totalTicketsSold || 0}
                    </p>
                  </div>
                  <Ticket className="w-10 h-10 text-purple-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Chiffre d'affaires total généré */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-8"
        >
          <Card className="glass-card bg-gradient-to-r from-primary/10 to-transparent">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Chiffre d'affaires généré par vos artistes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-4 bg-blue-500/10 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">CA EUR (Stripe)</p>
                  <p className="text-3xl font-bold text-blue-500" data-testid="ca-eur">
                    {formatPrice(dashboardData?.totalRevenueEUR || 0, 'EUR')}
                  </p>
                </div>
                <div className="p-4 bg-green-500/10 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">CA FCFA (Mobile Money)</p>
                  <p className="text-3xl font-bold text-green-500" data-testid="ca-fcfa">
                    {formatPrice(dashboardData?.totalRevenueFCFA || 0, 'FCFA')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Statut Stripe (si activé) */}
        {dashboardData?.apporteur?.stripe_connect_enabled && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="mb-8"
          >
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-primary" />
                  Paiements Stripe
                </CardTitle>
                <CardDescription>
                  Recevez vos commissions EUR directement sur votre compte bancaire
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {stripeStatus?.stripe_account_active ? (
                      <>
                        <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                          <Check className="w-5 h-5 text-green-500" />
                        </div>
                        <div>
                          <p className="font-medium text-green-500">Compte Stripe actif</p>
                          <p className="text-sm text-muted-foreground">Vos commissions EUR seront versées automatiquement</p>
                        </div>
                      </>
                    ) : stripeStatus?.stripe_account_id ? (
                      <>
                        <div className="w-10 h-10 bg-yellow-500/20 rounded-full flex items-center justify-center">
                          <AlertCircle className="w-5 h-5 text-yellow-500" />
                        </div>
                        <div>
                          <p className="font-medium text-yellow-500">Configuration incomplète</p>
                          <p className="text-sm text-muted-foreground">Veuillez compléter votre inscription Stripe</p>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
                          <CreditCard className="w-5 h-5 text-muted-foreground" />
                        </div>
                        <div>
                          <p className="font-medium">Non configuré</p>
                          <p className="text-sm text-muted-foreground">Configurez Stripe pour recevoir vos commissions EUR</p>
                        </div>
                      </>
                    )}
                  </div>
                  
                  {!stripeStatus?.stripe_account_active && (
                    <Button onClick={handleStripeOnboarding} className="btn-primary">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      {stripeStatus?.stripe_account_id ? 'Compléter' : 'Configurer Stripe'}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Liste des artistes parrainés */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-8"
        >
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Music className="w-5 h-5" />
                Artistes parrainés
              </CardTitle>
              <CardDescription>
                Liste des artistes qui ont utilisé votre code lors de leur inscription
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!dashboardData?.parrainedArtists || dashboardData.parrainedArtists.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Aucun artiste parrainé pour le moment</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Partagez votre code <strong>{dashboardData?.apporteur?.code}</strong> ou votre QR code pour commencer à parrainer des artistes
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {dashboardData.parrainedArtists.map((artist, index) => (
                    <motion.div
                      key={artist.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 * index }}
                      className="flex items-center justify-between p-4 bg-muted/50 rounded-lg hover:bg-muted/70 transition-colors"
                      data-testid={`artist-item-${index}`}
                    >
                      <div className="flex items-center gap-4">
                        {artist.profilePhotoUrl ? (
                          <img 
                            src={artist.profilePhotoUrl.startsWith('http') ? artist.profilePhotoUrl : `${process.env.REACT_APP_BACKEND_URL}${artist.profilePhotoUrl}`}
                            alt={artist.nomDeScene}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                            <Music className="w-6 h-6 text-primary" />
                          </div>
                        )}
                        <div>
                          <h4 className="font-bold">{artist.nomDeScene}</h4>
                          <p className="text-sm text-muted-foreground flex items-center gap-2">
                            <Calendar className="w-3 h-3" />
                            Parrainé le {formatDate(artist.apporteurAttributionAt)}
                          </p>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className="flex gap-2 text-sm">
                          <span className="text-blue-500">{formatPrice(artist.commissionEUR || 0, 'EUR')}</span>
                          <span className="text-muted-foreground">|</span>
                          <span className="text-green-500">{formatPrice(artist.commissionFCFA || 0, 'FCFA')}</span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {artist.ticketsSold || 0} billets vendus
                        </p>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(`/${artist.slug}`, '_blank')}
                          className="mt-1"
                        >
                          <ExternalLink className="w-4 h-4 mr-1" />
                          Voir la page
                        </Button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* QR Code Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <QrCode className="w-5 h-5" />
                Votre QR Code de parrainage
              </CardTitle>
              <CardDescription>
                Partagez ce QR code pour faciliter l'inscription des artistes avec votre code
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col items-center">
              {dashboardData?.apporteur?.qrCodeUrl ? (
                <>
                  <div className="p-4 bg-white rounded-lg shadow-lg">
                    <img 
                      src={dashboardData.apporteur.qrCodeUrl.startsWith('http') 
                        ? dashboardData.apporteur.qrCodeUrl 
                        : `${BACKEND_URL}${dashboardData.apporteur.qrCodeUrl}`}
                      alt="QR Code Parrainage"
                      className="w-48 h-48 object-contain"
                      data-testid="qr-code-image"
                      onError={(e) => {
                        console.error('QR Code failed to load:', e.target.src);
                        e.target.style.display = 'none';
                        e.target.nextSibling?.classList?.remove('hidden');
                      }}
                    />
                    <div className="hidden w-48 h-48 flex items-center justify-center bg-gray-100 rounded">
                      <QrCode className="w-16 h-16 text-gray-400" />
                    </div>
                  </div>
                  <p className="mt-4 text-sm text-muted-foreground text-center">
                    {language === 'fr' 
                      ? 'Scannez ce code pour accéder directement à l\'inscription avec votre code de parrainage'
                      : 'Scan this code to access registration with your referral code'}
                  </p>
                  <div className="mt-4 p-3 bg-muted/50 rounded-lg text-sm">
                    <p className="text-muted-foreground">URL de parrainage :</p>
                    <p className="font-mono text-primary break-all" data-testid="onboarding-url">
                      {dashboardData.apporteur.onboardingUrl}
                    </p>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <QrCode className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">QR Code non disponible</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Contactez l'administrateur pour générer votre QR code
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
};

export default ApporteurDashboard;
